import {
  type ActivityItem,
  type CoordinatorEmailStatus,
  type ExtraField,
  type InquiryRecord,
  type Stage,
  humanizeKey,
  parseEventDate,
  parseGuestCount,
} from './model';

/**
 * Shape returned by /api/event-inquiries/ and /api/catering-inquiries/
 * (list + detail serializers in event_inquiries/serializers.py).
 */
export interface ApiInquiry {
  id: number;
  store?: number;
  store_name?: string | null;
  retell_call_id?: string | null;
  caller_phone?: string | null;
  status?: string | null;
  dashboard_state?: string | null;
  dashboard_state_updated_at?: string | null;
  dashboard_state_updated_by_email?: string | null;
  guest_name?: string | null;
  phone?: string | null;
  email?: string | null;
  occasion?: string | null;
  preferred_date?: string | null;
  guest_count?: string | number | null;
  preferred_time?: string | null;
  special_requests?: string | null;
  notes?: string | null;
  ai_generated_summary?: string | null;
  follow_up_notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  sent_at?: string | null;
  // detail only
  submitted_fields?: Record<string, unknown> | null;
  parsed_fields?: Record<string, unknown> | null;
  transcript?: string | null;
  ai_transcript?: string | null;
  email_subject?: string | null;
  email_body?: string | null;
  email_recipients?: string[] | null;
  error?: string | null;
}

export interface ApiSummary {
  total?: number;
  visible_total?: number;
  unseen?: number;
  seen?: number;
  won?: number;
  hidden?: number;
  archived?: number;
  processing?: Partial<Record<CoordinatorEmailStatus, number>>;
}

const str = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
};

function emailStatus(s?: string | null): CoordinatorEmailStatus {
  const n = (s || '').toLowerCase();
  return n === 'sent' || n === 'failed' || n === 'processing' ? n : 'pending';
}

export function deriveStage(api: Pick<ApiInquiry, 'dashboard_state'>): Stage {
  const ds = (api.dashboard_state || 'unseen').toLowerCase();
  if (ds === 'hidden') return 'lost';
  if (ds === 'unseen') return 'new';
  if (ds === 'won') return 'won';
  if (ds === 'archived') return 'archived';
  return 'to_contact';
}

/**
 * Parsed-field keys that already feed a column or the summary. Anything else
 * Arthur captured (older prompts asked for seating, dietary needs, drop-off
 * address…) is listed under the details so it stays visible.
 */
const SHOWN_PARSED_KEYS = new Set([
  'event_type',
  'event_date',
  'event_time',
  'catering_type',
  'catering_date',
  'catering_time',
  'guest_count',
  'contact_name',
  'contact_phone',
  'contact_email',
  'preferred_contact',
  'budget',
  'special_requests',
  'ai_generated_summary',
  'notes',
  'missing_information',
  'confidence',
]);

function displayValue(v: unknown): string | null {
  if (Array.isArray(v))
    return str(
      v
        .map((x) => str(x))
        .filter(Boolean)
        .join(', '),
    );
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (v != null && typeof v === 'object') return null;
  return str(v);
}

export function extraParsedFields(
  pf: Record<string, unknown> | null | undefined,
): ExtraField[] {
  return Object.entries(pf || {})
    .filter(([k]) => !SHOWN_PARSED_KEYS.has(k))
    .map(([k, v]) => {
      const value = displayValue(v);
      const label = humanizeKey(k);
      return value
        ? {
            key: k,
            label: label.charAt(0).toUpperCase() + label.slice(1),
            value,
          }
        : null;
    })
    .filter((f): f is ExtraField => f != null);
}

export function fromApi(api: ApiInquiry): InquiryRecord {
  const pf = (api.parsed_fields || {}) as Record<string, unknown>;
  const eventDateRaw =
    str(api.preferred_date) ?? str(pf.event_date) ?? str(pf.catering_date);
  const guestCountRaw = str(api.guest_count) ?? str(pf.guest_count);
  const createdAt = api.created_at || new Date().toISOString();
  const status = emailStatus(api.status);
  const transcript = str(api.ai_transcript) ?? str(api.transcript);
  const recipients = (api.email_recipients || []).filter(Boolean);

  const activity: ActivityItem[] = [];
  if (api.retell_call_id || transcript) {
    activity.push({
      id: `call-${api.id}`,
      kind: 'call',
      at: createdAt,
      actor: 'arthur',
      title: 'Arthur took the inquiry by phone',
      transcript,
      meta: { from: api.caller_phone || undefined },
    });
  } else {
    activity.push({
      id: `created-${api.id}`,
      kind: 'system',
      at: createdAt,
      actor: 'system',
      title: 'Inquiry received',
    });
  }
  if (status === 'failed') {
    activity.push({
      id: `email-${api.id}`,
      kind: 'email',
      at: api.updated_at || createdAt,
      actor: 'system',
      title: 'Team email failed to send',
      body: str(api.error),
    });
  } else if (status === 'sent' || api.email_body || api.email_subject) {
    activity.push({
      id: `email-${api.id}`,
      kind: 'email',
      at: api.sent_at || api.updated_at || createdAt,
      actor: 'arthur',
      title:
        status === 'sent'
          ? `Summary emailed to ${recipients.length ? recipients.join(', ') : 'the team'}`
          : 'Team email drafted, not sent yet',
      body: str(api.email_body),
      meta: { subject: str(api.email_subject) ?? undefined },
    });
  }
  if (api.dashboard_state === 'seen' && api.dashboard_state_updated_at) {
    activity.push({
      id: `seen-${api.id}`,
      kind: 'system',
      at: api.dashboard_state_updated_at,
      actor: 'system',
      title: api.dashboard_state_updated_by_email
        ? `Opened by ${api.dashboard_state_updated_by_email}`
        : 'Marked as seen',
    });
  }
  if (api.dashboard_state === 'hidden' && api.dashboard_state_updated_at) {
    activity.push({
      id: `lost-${api.id}`,
      kind: 'system',
      at: api.dashboard_state_updated_at,
      actor: 'system',
      title: api.dashboard_state_updated_by_email
        ? `Marked as lost by ${api.dashboard_state_updated_by_email}`
        : 'Marked as lost',
    });
  }
  if (api.dashboard_state === 'won' && api.dashboard_state_updated_at) {
    activity.push({
      id: `won-${api.id}`,
      kind: 'system',
      at: api.dashboard_state_updated_at,
      actor: 'system',
      title: api.dashboard_state_updated_by_email
        ? `Marked as won by ${api.dashboard_state_updated_by_email}`
        : 'Marked as won',
    });
  }
  if (api.dashboard_state === 'archived' && api.dashboard_state_updated_at) {
    activity.push({
      id: `archived-${api.id}`,
      kind: 'system',
      at: api.dashboard_state_updated_at,
      actor: 'system',
      title: api.dashboard_state_updated_by_email
        ? `Archived by ${api.dashboard_state_updated_by_email}`
        : 'Archived',
    });
  }
  const followUp = str(api.follow_up_notes);
  if (followUp) {
    activity.push({
      id: `note-${api.id}`,
      kind: 'note',
      at: api.updated_at || createdAt,
      actor: { name: 'Team' },
      title: 'Follow-up notes',
      body: followUp,
    });
  }
  activity.sort((a, b) => a.at.localeCompare(b.at));

  return {
    id: api.id,
    guestName: str(api.guest_name) ?? str(pf.contact_name),
    phone: str(api.phone) ?? str(pf.contact_phone) ?? str(api.caller_phone),
    email: str(api.email) ?? str(pf.contact_email),
    preferredContact: str(pf.preferred_contact),
    occasion: str(api.occasion) ?? str(pf.event_type) ?? str(pf.catering_type),
    eventDate: parseEventDate(eventDateRaw),
    eventDateRaw,
    eventTime:
      str(api.preferred_time) ?? str(pf.event_time) ?? str(pf.catering_time),
    guestCount: parseGuestCount(guestCountRaw),
    guestCountRaw,
    budget: str(pf.budget),
    specialRequests: str(api.special_requests) ?? str(pf.special_requests),
    summary:
      str(api.ai_generated_summary) ??
      str(api.notes) ??
      str(pf.ai_generated_summary),
    missingInformation: Array.isArray(pf.missing_information)
      ? (pf.missing_information as unknown[])
          .map((k) => humanizeKey(String(k)))
          .filter(Boolean)
      : [],
    extraFields: extraParsedFields(pf),
    transcript,
    followUpNotes: followUp,
    stage: deriveStage(api),
    source: api.retell_call_id ? 'call' : 'manual',
    retellCallId: str(api.retell_call_id),
    callerPhone: str(api.caller_phone),
    coordinatorEmail: {
      status,
      subject: str(api.email_subject),
      body: str(api.email_body),
      recipients,
      sentAt: api.sent_at || null,
      error: str(api.error),
    },
    createdAt,
    updatedAt: api.updated_at || createdAt,
    activity,
  };
}

export function fromApiSummary(s: ApiSummary) {
  return {
    total: s.total ?? 0,
    visible: s.visible_total ?? 0,
    unseen: s.unseen ?? 0,
    seen: s.seen ?? 0,
    won: s.won ?? 0,
    hidden: s.hidden ?? 0,
    archived: s.archived ?? 0,
    failedEmail: s.processing?.failed ?? 0,
  };
}
